import { V2Button } from '@abyss/web/ui/Button';
import { Layout } from '@abyss/web/ui/Layout';
import { V2ModalDialog } from '@abyss/web/ui/ModalDialog';
import { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  signInUrl: string;
  uhcContinueUrl: string;
};

export const GuestPlanRequiredModal = ({
  isOpen,
  onClose,
  signInUrl,
  uhcContinueUrl,
}: Props) => {
  const { t } = useTranslation();

  const headerText = t('GUEST_PRE_PLAN_SELECTION.TITLE', {
    defaultValue: 'Continue as Guest or Sign In?',
  });

  const bodyText = t('GUEST_PRE_PLAN_SELECTION.CONTENT', {
    defaultValue:
      "You're now leaving the Guest experience. Sign in for a more personalized experience or continue to uhc.com.",
  });

  // Apply 16px padding to the modal header container (outer div) inline, robust to portals
  useLayoutEffect(() => {
    function applyPadding(el: HTMLElement) {
      el.style.setProperty('padding', '16px', 'important');
    }

    function findHeader(): HTMLElement | null {
      return (
        (document.querySelector(
          '[data-testid="guest-plan-required-modal-abyss-modal-base-header-container"]'
        ) as HTMLElement | null) ||
        (document.querySelector(
          '.abyss-modal-base-header-container'
        ) as HTMLElement | null)
      );
    }

    const observer = new MutationObserver(() => {
      const el = findHeader();
      if (el) applyPadding(el);
    });
    observer.observe(document.body, { childList: true, subtree: true });

    if (isOpen) {
      const target = findHeader();
      if (target) applyPadding(target);
    }

    return () => {
      observer.disconnect();
      const target = findHeader();
      if (target) {
        target.style.removeProperty('padding');
      }
    };
  }, [isOpen]);
  return (
    <V2ModalDialog
      className="guest-plan-required-modal"
      closeOnClickOutside={false}
      data-auto-testid="guest-plan-required-modal"
      data-testid="guest-plan-required-modal"
      footer={
        <Layout.Group alignLayout="right" style={{ gap: 16 }}>
          <V2Button
            data-testid="guest-plan-required-sign-in"
            onClick={() => window.location.assign(signInUrl)}
          >
            {t('GUEST_PRE_PLAN_SELECTION.SIGN_IN')}
          </V2Button>
          <V2Button
            data-testid="guest-plan-required-continue"
            onClick={() => window.location.assign(uhcContinueUrl)}
            variant="outline"
          >
            {t('GUEST_PRE_PLAN_SELECTION.CONTINUE')}
          </V2Button>
        </Layout.Group>
      }
      header={headerText}
      isOpen={isOpen}
      onClose={onClose}
    >
      <div data-testid="guest-plan-required-modal-abyss-modal-body">
        {bodyText}
      </div>
    </V2ModalDialog>
  );
};

export default GuestPlanRequiredModal;
